import React, { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion'
import axios from "axios";

const baseURL: string = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://api.comment8.ai/api'
    : 'https://staging-api.comment8.ai/api');

// ============ NAVBAR ============
const Navbar = ({ onJoinWaitlist }: { onJoinWaitlist: () => void }) => {
  const [showFullNav, setShowFullNav] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      // Get the "Why Comment8" section (about section)
      const aboutSection = document.getElementById('about')
      if (aboutSection) {
        const sectionTop = aboutSection.offsetTop
        setShowFullNav(window.scrollY >= sectionTop - 100)
      }
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          showFullNav ? 'bg-white/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - always visible, animates when reaching section */}
            <a href="#" className="flex items-center space-x-3">
              <motion.img
                src="/assets/comment8/logo.svg"
                alt="Comment8"
                className="h-8 w-auto"
                animate={showFullNav ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                } : {}}
                transition={{ duration: 0.5 }}
              />
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: showFullNav ? 1 : 0,
                  x: showFullNav ? 0 : -20
                }}
                transition={{ duration: 0.3 }}
                className="text-xl font-medium text-c8-text"
              >
                Comment8
              </motion.span>
            </a>

            {/* Desktop: Nav links - hidden until reaching section */}
            <motion.div
              className="hidden md:flex items-center space-x-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{
                opacity: showFullNav ? 1 : 0,
                y: showFullNav ? 0 : -20,
                pointerEvents: showFullNav ? 'auto' : 'none'
              }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {['Features', 'How It Works', 'About'].map((label) => (
                <a key={label} href={`#${label.toLowerCase().replace(' ', '-')}`}
                   className="text-sm font-medium text-c8-text hover:text-c8-orange transition-colors duration-300"
                >{label}</a>
              ))}
            </motion.div>

            {/* Join Waitlist button - always visible on far right */}
            <button
              onClick={onJoinWaitlist}
              className="relative px-4 py-2 md:px-6 md:py-2.5 text-xs md:text-sm font-bold rounded-full transition-all duration-300 transform hover:scale-105"
              style={{
                background: 'transparent',
                border: '2px solid #FF6B35',
                color: '#FF6B35',
              }}
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </motion.nav>
      <AnimatePresence>
        {isMobileMenuOpen && showFullNav && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-30 bg-white pt-24 px-6 md:hidden">
            <div className="flex flex-col space-y-6">
              {['Features', 'How It Works', 'About'].map((label) => (
                <a key={label} href={`#${label.toLowerCase().replace(' ', '-')}`}
                   onClick={() => setIsMobileMenuOpen(false)}
                   className="text-2xl font-semibold text-c8-text">{label}</a>
              ))}
              <button
                onClick={() => { setIsMobileMenuOpen(false); onJoinWaitlist(); }}
                className="btn-primary text-center mt-4 w-full"
              >
                Join Waitlist
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

// ============ HERO QUESTIONS ============
const HeroQuestions = () => {
  const containerRef = useRef(null)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end start'] })

  // Timing adjusted so all animations complete before section ends
  const opacity1 = useTransform(scrollYProgress, [0, 0.15, 0.2], [1, 1, 0])
  const opacity2 = useTransform(scrollYProgress, [0.15, 0.2, 0.4, 0.45], [0, 1, 1, 0])
  const opacity3 = useTransform(scrollYProgress, [0.4, 0.45, 0.9, 0.95], [0, 1, 1, 0])

  // Typing animation for third slide - "Comment8 lets you answer."
  const typingText = "Comment8 lets you answer."
  const typingProgress = useTransform(scrollYProgress, [0.5, 0.75], [0, typingText.length])
  const [displayedChars, setDisplayedChars] = useState(0)

  useEffect(() => {
    const unsubscribe = typingProgress.on('change', (v) => {
      setDisplayedChars(Math.floor(v))
    })
    return () => unsubscribe()
  }, [typingProgress])

  // Highlight animation transforms for first slide
  const highlightWidth = useTransform(scrollYProgress, [0.03, 0.1], ['0%', '100%'])
  const highlightOpacity = useTransform(scrollYProgress, [0.03, 0.05], [0, 1])
  const logoOpacity = useTransform(scrollYProgress, [0.08, 0.12], [0, 1])
  const logoScale = useTransform(scrollYProgress, [0.08, 0.12], [0.5, 1])

  // Orange text opacity for second slide
  const orangeTextOpacity = useTransform(scrollYProgress, [0.25, 0.35], [0.3, 1])

  return (
    <section
      ref={containerRef}
      className="relative"
      style={{ height: '500vh', backgroundColor: '#0a0a0a' }}
    >
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background layers inside sticky */}
        <div className="absolute inset-0 bg-gradient-to-br from-c8-dark via-neutral-900 to-black" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] sm:w-[400px] md:w-[600px] h-[250px] sm:h-[400px] md:h-[600px] bg-c8-orange/10 rounded-full blur-[80px] sm:blur-[120px] md:blur-[150px]" />
        <div className="absolute inset-0 bg-black/50" />

        {/* Fixed tagline at top - centerpiece */}
        <div className="absolute top-28 sm:top-32 md:top-36 left-0 right-0 z-20 px-4 sm:px-6">
          <p className="text-center text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-bold max-w-4xl mx-auto leading-relaxed tracking-wide"
             style={{
               textShadow: '0 2px 20px rgba(255,107,53,0.3), 0 4px 40px rgba(0,0,0,0.5)',
             }}
          >
            A social media platform centered around the internet without moderators, influencers and bots.
          </p>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center">
          {/* First slide - with highlight animation */}
          <motion.div style={{ opacity: opacity1 }}
                      className="absolute inset-0 flex flex-col items-center justify-center space-y-4 sm:space-y-6 px-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white text-center leading-tight">
              The internet never let you talk back.
            </h1>
            <div className="w-20 sm:w-32 h-1 sm:h-1.5 bg-white rounded-full" />

            {/* Spacer + Highlighted text container */}
            <div className="inline-flex items-center justify-center">
              {/* Spacer to shift right - outside highlight */}
              <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-transparent select-none">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>

              {/* Highlighted text */}
              <div className="relative inline-flex items-center">
                {/* Blue highlight background - animates width */}
                <motion.div
                  className="absolute bg-[#3390FF]/80 rounded-lg"
                  style={{
                    width: highlightWidth,
                    opacity: highlightOpacity,
                    top: '-0.5rem',
                    bottom: '-0.5rem',
                    left: '-0.5rem',
                    right: '-0.5rem'
                  }}
                />

                {/* The text */}
                <h1 className="relative text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white px-2 sm:px-4 py-2">
                  Now you can.
                </h1>

                {/* Space between text and logo */}
                <span>&nbsp;</span>

                {/* Comment8 logo - inside highlight at the end */}
                <motion.div
                  className="relative"
                  style={{
                    opacity: logoOpacity,
                    scale: logoScale
                  }}
                >
                  <div className="bg-white rounded-full p-2 shadow-lg">
                    <img src="/assets/comment8/logo.svg" alt="Comment8" className="w-8 h-8 md:w-10 md:h-10" />
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Second slide */}
          <motion.div style={{ opacity: opacity2 }}
                      className="absolute inset-0 flex flex-col items-center justify-center space-y-4 px-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white text-center leading-tight">
              Comments belong where ideas happen.
            </h1>
            {/* Simple fade-in orange text */}
            <motion.h1
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-center text-c8-orange"
              style={{ opacity: orangeTextOpacity }}
            >
              Not buried in feeds.
            </motion.h1>
          </motion.div>

          {/* Third slide - orange with typing animation */}
          <motion.div style={{ opacity: opacity3 }}
                      className="absolute inset-0 flex flex-col items-center justify-center space-y-4 px-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold italic text-white text-center">
              The web learned to talk.
            </h1>
            <div className="relative">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-c8-orange text-center">
                {typingText.slice(0, displayedChars)}
                {/* Blinking cursor */}
                {displayedChars < typingText.length && (
                  <motion.span
                    className="inline-block w-[2px] sm:w-[3px] md:w-[4px] h-[1.1em] bg-c8-orange ml-1 align-middle"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                )}
              </h1>
              {/* Invisible text to maintain width */}
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-transparent text-center absolute inset-0" aria-hidden="true">
                {typingText}
              </h1>
            </div>
          </motion.div>
        </div>
        <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
                      className="flex flex-col items-center text-white/50">
            <span className="text-sm uppercase tracking-widest">Scroll</span>
            <svg className="w-6 h-6 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

// ============ CINEMATIC TRAILER ============
const CinematicTrailer = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [isMuted, setIsMuted] = useState(true)

  const videoBaseUrl = "https://www.youtube.com/embed/rgiNdPNp820"
  const videoParams = `autoplay=1&loop=1&playlist=rgiNdPNp820&controls=0&showinfo=0&rel=0&modestbranding=1&mute=${isMuted ? 1 : 0}`

  return (
    <section ref={ref} id="about" className="relative min-h-screen bg-c8-dark py-24 md:py-32 overflow-hidden scroll-mt-0" style={{ scrollSnapAlign: 'start' }}>
      <div className="absolute inset-0 bg-gradient-to-b from-black via-c8-dark to-c8-dark" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-c8-orange/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }} className="text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Why <span className="text-c8-orange">Comment8</span>
          </h2>
          <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto font-light">
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }} className="relative rounded-2xl overflow-hidden shadow-2xl">
          <div className="absolute -inset-1 bg-gradient-to-r from-c8-orange/20 via-c8-orange/10 to-c8-orange/20 rounded-2xl blur-sm" />
          <div className="relative bg-black rounded-2xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`${videoBaseUrl}?${videoParams}`}
              title="Comment8 Trailer"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />

            {/* Mute/Unmute Button */}
            <motion.button
              onClick={() => setIsMuted(!isMuted)}
              className="absolute bottom-4 right-4 z-10 flex items-center gap-2 px-4 py-2 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium hover:bg-c8-orange/80 hover:border-c8-orange transition-all duration-300 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isMuted ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                  <span>Unmute</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                  <span>Mute</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ============ SOLUTION REVEAL ============
const SolutionReveal = ({ onJoinWaitlist }: { onJoinWaitlist: () => void }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  return (
    <section ref={ref} className="relative min-h-screen bg-c8-bg py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-c8-dark via-c8-bg to-c8-bg" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 sm:gap-20 lg:gap-20 items-center min-h-[70vh]">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.8 }}>
            <div
              className="relative rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #FF7A1A 0%, #FF6A00 25%, #E85D00 50%, #FF6A00 75%, #FF7A1A 100%)',
                boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.2), 0 25px 50px -12px rgba(0,0,0,0.4)',
              }}
            >
              {/* Leather texture overlay */}
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                }}
              />
              {/* Subtle grain */}
              <div
                className="absolute inset-0 opacity-10 pointer-events-none mix-blend-overlay"
                style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                }}
              />
              {/* Inner highlight edge */}
              <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.2)',
              }} />

              {/* Heading with silver inlay and embossed text */}
              <h2 className="section-heading mb-6 relative">
                <span
                  style={{
                    background: 'linear-gradient(90deg, #F5F5F5 0%, #FFFFFF 20%, #E8E8E8 40%, #FFFFFF 50%, #F0F0F0 60%, #FFFFFF 80%, #F5F5F5 100%)',
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(1px 1px 0px rgba(0,0,0,0.3)) drop-shadow(0 0 1px rgba(255,255,255,0.5))',
                    animation: 'silverShine 4s ease-in-out infinite',
                  }}
                >
                  A comment layer for the
                </span>{' '}
                <span
                  style={{
                    color: 'rgba(0,0,0,0.8)',
                    textShadow: '1px 1px 1px rgba(255,255,255,0.4), -1px -1px 1px rgba(0,0,0,0.2)',
                  }}
                >
                  entire internet.
                </span>
              </h2>

              {/* Embossed paragraph */}
              <p
                className="text-lg md:text-xl font-bold leading-relaxed mb-4 relative"
                style={{
                  color: 'rgba(0,0,0,0.55)',
                  textShadow: '1px 1px 0px rgba(255,255,255,0.3), -0.5px -0.5px 0px rgba(0,0,0,0.15)',
                }}
              >
                Comment8 lets you leave thoughts, questions, and ideas directly on any website. Your comments stay anchored to the exact content that inspired them, so context is never lost.
              </p>
              <p
                className="text-lg md:text-xl font-normal leading-relaxed mb-8 relative"
                style={{
                  color: 'rgba(0,0,0,0.55)',
                  textShadow: '1px 1px 0px rgba(255,255,255,0.3), -0.5px -0.5px 0px rgba(0,0,0,0.15)',
                }}
              >
                Follow people who think differently. Take the conversation into DMs. Discover new perspectives in a brilliant newsfeed.
              </p>

              {/* Polished Rolex-style stainless steel button */}
              <div className="flex justify-center">
                <button
                  onClick={onJoinWaitlist}
                  className="group relative inline-flex items-center space-x-2 px-10 py-4 font-semibold rounded-full transition-all duration-300 transform hover:scale-105 overflow-hidden"
                  style={{
                    background: 'linear-gradient(165deg, #FAFAFA 0%, #F0F0F0 10%, #E8E8E8 20%, #FFFFFF 35%, #E0E0E0 50%, #D8D8D8 65%, #E8E8E8 80%, #F5F5F5 90%, #FFFFFF 100%)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.25), 0 4px 8px rgba(0,0,0,0.15), inset 0 1px 2px rgba(255,255,255,1), inset 0 -1px 1px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(200,200,200,0.5)',
                  }}
                >
                  {/* Mirror shine sweep */}
                  <div
                    className="absolute inset-0 pointer-events-none opacity-60"
                    style={{
                      background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.8) 45%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.8) 55%, transparent 60%)',
                      animation: 'rolexShine 3s ease-in-out infinite',
                    }}
                  />
                  <span
                    className="relative"
                    style={{
                      color: '#1a1a1a',
                      textShadow: '0 1px 1px rgba(255,255,255,0.8)',
                      letterSpacing: '0.3px',
                    }}
                  >
                  Get Comment8
                </span>
                  <svg
                    className="w-5 h-5 relative"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      color: '#1a1a1a',
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="flex justify-center lg:justify-end">
            <div className="relative flex justify-center">
              {/* Glow effect */}
              <div className="absolute -inset-8 bg-gradient-to-tr from-c8-orange/25 to-orange-300/15 rounded-[60px] blur-2xl" />

              {/* iPhone frame - responsive sizing */}
              <div
                className="relative w-[260px] h-[530px] sm:w-[300px] sm:h-[612px] md:w-[340px] md:h-[693px] lg:w-[380px] lg:h-[775px]"
                style={{
                  background: 'linear-gradient(145deg, #2A2A2A 0%, #1A1A1A 50%, #0D0D0D 100%)',
                  borderRadius: '12.5%/6.2%',
                  padding: '3%',
                  boxShadow: '0 50px 100px -20px rgba(0,0,0,0.4), 0 30px 60px -30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.3)',
                }}
              >
                {/* Side buttons - volume (positioned with percentages) */}
                <div className="absolute -left-[3px] top-[18%] w-[4px] h-[5%] bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-l-sm" />
                <div className="absolute -left-[3px] top-[24%] w-[4px] h-[8%] bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-l-sm" />
                <div className="absolute -left-[3px] top-[34%] w-[4px] h-[8%] bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] rounded-l-sm" />
                {/* Side button - power */}
                <div className="absolute -right-[3px] top-[27%] w-[4px] h-[12%] bg-gradient-to-l from-[#1a1a1a] to-[#2a2a2a] rounded-r-sm" />

                {/* Inner bezel */}
                <div
                  className="relative w-full h-full overflow-hidden bg-black"
                  style={{
                    borderRadius: '10%/5%',
                  }}
                >
                  {/* Dynamic Island */}
                  <div
                    className="absolute top-[1.5%] left-1/2 -translate-x-1/2 z-10 w-[33%] h-[4%] bg-black"
                    style={{
                      borderRadius: '20px',
                    }}
                  />

                  {/* Screen content - uses aspect ratio matching iPhone screen */}
                  <div
                    className="absolute inset-0 overflow-hidden"
                    style={{
                      borderRadius: '10%/5%',
                    }}
                  >
                    <img
                      src="/assets/comment8/feed-view.png"
                      alt="Comment8 mobile app"
                      className="w-full h-full object-cover object-top"
                      style={{
                        objectPosition: 'top center',
                      }}
                    />
                  </div>

                  {/* Screen reflection */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      borderRadius: '10%/5%',
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 35%, transparent 100%)',
                    }}
                  />
                </div>

                {/* Frame highlight */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: '12.5%/6.2%',
                    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.15), inset 0 -1px 1px rgba(0,0,0,0.3)',
                  }}
                />
              </div>

              {/* Context preserved label */}
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}
                          className="absolute -top-4 -right-2 sm:-top-6 sm:-right-4 bg-white text-c8-orange px-4 py-2 sm:px-6 sm:py-3 rounded-full text-sm sm:text-lg font-bold shadow-lg border-2 border-c8-orange/20 z-20">
                Context preserved
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============ FEATURE BREAKDOWN ============
const FeatureBreakdown = () => {
  const containerRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] })
  const [activeIndex, setActiveIndex] = useState(0)

  const features = [
    { id: 'explore', title: 'Explore', subtitle: 'Other minds, same page', desc: "See others' perspectives, exactly where they left them.", img: '/assets/comment8/explore.png' },
    { id: 'newsfeed', title: 'Newsfeed', subtitle: 'Be Inspired', desc: 'When conversations unfold across the web, your newsfeed brings them together by topic.', img: '/assets/comment8/newsfeed.png' },
    { id: 'connections', title: 'Connections', subtitle: 'Connection that compounds', desc: 'Unlike one-off messages and forgotten saves, Comment8 preserves conversations across people, posts, and topics.', img: '/assets/comment8/messages.png' },
  ]

  // Sync active index with scroll
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (v) => {
      if (v < 0.33) setActiveIndex(0)
      else if (v < 0.66) setActiveIndex(1)
      else setActiveIndex(2)
    })
    return () => unsubscribe()
  }, [scrollYProgress])

  // Click handler to scroll to specific card
  const scrollToCard = (index: number) => {
    if (!containerRef.current) return
    const sectionTop = containerRef.current.offsetTop
    const sectionHeight = containerRef.current.offsetHeight - window.innerHeight
    const targetScroll = sectionTop + (sectionHeight * (index * 0.33 + 0.1))
    window.scrollTo({ top: targetScroll, behavior: 'smooth' })
  }

  return (
    <section
      ref={containerRef}
      id="features"
      className="relative"
      style={{
        height: '300vh',
        background: `
          radial-gradient(ellipse 120% 80% at 20% 30%, rgba(215,205,195,0.15) 0%, transparent 50%),
          radial-gradient(ellipse 100% 60% at 80% 70%, rgba(200,190,180,0.12) 0%, transparent 50%),
          radial-gradient(ellipse 80% 100% at 50% 50%, rgba(210,200,190,0.08) 0%, transparent 60%),
          linear-gradient(145deg, #FAF8F5 0%, #F5F2ED 25%, #F0EDE8 50%, #F5F2ED 75%, #FAF8F5 100%)
        `,
        backgroundAttachment: 'fixed',
      }}
    >

      {/* Stitching borders - sticky so they stay visible while scrolling through section */}
      <div className="sticky top-0 left-0 right-0 h-0 z-20 pointer-events-none">
        {/* Top stitch */}
        <div className="absolute top-3 md:top-4 left-3 right-3 md:left-6 md:right-6 h-[2px]"
             style={{
               backgroundImage: 'repeating-linear-gradient(90deg, #A69080 0px, #A69080 6px, transparent 6px, transparent 12px)',
               opacity: 0.5,
             }}
        />
        {/* Left stitch */}
        <div className="absolute top-3 md:top-6 left-3 md:left-4 w-[2px] h-screen"
             style={{
               backgroundImage: 'repeating-linear-gradient(180deg, #A69080 0px, #A69080 6px, transparent 6px, transparent 12px)',
               opacity: 0.5,
             }}
        />
        {/* Right stitch */}
        <div className="absolute top-3 md:top-6 right-3 md:right-4 w-[2px] h-screen"
             style={{
               backgroundImage: 'repeating-linear-gradient(180deg, #A69080 0px, #A69080 6px, transparent 6px, transparent 12px)',
               opacity: 0.5,
             }}
        />
      </div>

      {/* Sticky container */}
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Navigation bar */}
        <div
          className="pt-20 md:pt-24 pb-4 md:pb-6"
          style={{
            background: 'linear-gradient(180deg, #FAF8F5 0%, #F5F2ED 100%)',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center space-x-6 sm:space-x-8 md:space-x-12">
              {features.map((f, index) => (
                <button
                  key={f.id}
                  onClick={() => scrollToCard(index)}
                  className="relative group"
                >
                  <span
                    className={`text-sm sm:text-base md:text-lg pb-2 block cursor-pointer ${
                      activeIndex === index
                        ? 'text-c8-orange font-bold'
                        : 'text-c8-text-secondary font-medium hover:text-c8-text'
                    }`}
                  >
                    {f.title}
                  </span>
                  {/* Underline - full when active */}
                  <div
                    className={`absolute bottom-0 left-0 h-0.5 bg-c8-orange transition-all ${
                      activeIndex === index ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Card content - shows one card at a time */}
        <div className="h-[calc(100vh-120px)] flex items-center py-4 mt-16 sm:mt-20">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* Mobile: stacked vertically, text first. Desktop: side by side */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

                {/* Text content */}
                <div className="text-center lg:text-left">
                  <span className="text-c8-orange font-semibold text-base sm:text-lg md:text-xl uppercase tracking-wider">
                    {features[activeIndex].subtitle}
                  </span>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-c8-text mt-2 mb-4">
                    {features[activeIndex].title}
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl text-c8-text-secondary leading-relaxed">
                    {features[activeIndex].desc}
                  </p>
                </div>

                {/* Image */}
                <div className="flex justify-center lg:justify-end">
                  <div className="relative">
                    {/* Soft orange glow behind */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-c8-orange via-orange-400 to-c8-orange rounded-2xl opacity-60 blur-md" />

                    {/* Clean white card with image */}
                    <div className="relative bg-white rounded-xl overflow-hidden shadow-2xl border-2 border-c8-orange/30">
                      <img
                        src={features[activeIndex].img}
                        alt={features[activeIndex].title}
                        className="w-full h-auto max-w-[300px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-[450px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress indicators - clickable */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToCard(index)}
              className={`rounded-full transition-all duration-300 cursor-pointer hover:bg-c8-orange ${
                activeIndex === index
                  ? 'w-6 h-1.5 bg-c8-orange'
                  : 'w-1.5 h-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

// ============ WHY COMMENT8 ============
const WhyComment8 = () => {
  const advantages = [
    { title: 'Context that sticks', desc: 'Your thoughts stay anchored to exactly where they were formed.' },
    { title: 'Less friction', desc: 'No accounts, no switching apps. Comment instantly on any content.' },
    { title: 'Shared understanding', desc: 'See what others think about the same content.' },
    { title: 'Deeper reading', desc: 'Active annotation transforms passive consumption into active thinking.' },
  ]

  return (
    <section className="py-24 md:py-32 bg-c8-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} className="text-center mb-16">
          <h2 className="section-heading text-c8-text mb-6">Why <span className="text-c8-orange">Comment8</span>?</h2>
          <p className="section-subheading max-w-2xl mx-auto">Built for how you actually think online.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {advantages.map((adv, index) => (
            <motion.div key={adv.title} initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="group bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10 shadow-lg border border-gray-100 hover:shadow-xl hover:border-c8-orange/20 transition-all active:scale-[0.98]">
              <h3 className="text-xl sm:text-2xl font-bold text-c8-text mb-3 sm:mb-4">{adv.title}</h3>
              <p className="text-c8-text-secondary leading-relaxed text-sm sm:text-base">{adv.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============ HOW IT WORKS ============
const HowItWorks = () => {
  const [isMobile, setIsMobile] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handlePlayFullscreen = () => {
    const video = videoRef.current
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if ((video as any).webkitEnterFullscreen) {
        (video as any).webkitEnterFullscreen()
      } else if ((video as any).webkitRequestFullscreen) {
        (video as any).webkitRequestFullscreen()
      }
      video.play()
    }
  }

  const steps = [
    { num: '01', title: 'Install', subtitle: 'Browser or mobile', desc: 'Add Comment8 to Chrome, Safari, Firefox, or download our iOS/Android app.' },
    { num: '02', title: 'Browse', subtitle: 'Subtle highlights appear', desc: 'As you browse, Comment8 shows where others have left thoughts.' },
    { num: '03', title: 'Comment', subtitle: 'Or generate with AI', desc: "Select any text and share your thought. Or let AI help you articulate." },
    { num: '04', title: 'Return', subtitle: 'Context stays', desc: 'Your comments are always there, exactly where you left them.' },
  ]

  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-[#1B1B1B]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">How it <span className="text-c8-orange">works</span></h2>
        </motion.div>

        {/* Steps Grid - Leather styled cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-20">
          {steps.map((step, index) => (
            <motion.div key={step.num} initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="relative p-5 sm:p-6 rounded-xl sm:rounded-2xl overflow-hidden transition-all hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          background: 'linear-gradient(145deg, #2A2A2A 0%, #232323 25%, #1E1E1E 50%, #232323 75%, #2A2A2A 100%)',
                          boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.05), inset 0 -2px 4px rgba(0,0,0,0.3), 0 10px 30px rgba(0,0,0,0.5)',
                        }}
            >
              {/* Leather texture overlay */}
              <div
                className="absolute inset-0 opacity-40 pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                }}
              />
              {/* Subtle grain */}
              <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.02) 2px, rgba(255,255,255,0.02) 4px)',
                }}
              />
              {/* Inner edge highlight */}
              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.3)',
              }} />

              <div className="relative">
                <div className="w-12 h-12 bg-c8-orange text-white rounded-full flex items-center justify-center font-bold mb-4 shadow-lg">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-white mb-1" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{step.title}</h3>
                <p className="text-c8-orange text-sm font-semibold mb-2">{step.subtitle}</p>
                <p className="text-white/80 text-sm font-medium leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Large Video */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }} transition={{ duration: 0.8 }}
                    className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-c8-orange/20 to-orange-200/5 rounded-3xl blur-3xl" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            {isMobile ? (
              /* Mobile: Click to play fullscreen */
              <div className="relative cursor-pointer" onClick={handlePlayFullscreen}>
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="w-full h-auto"
                  poster="/assets/comment8/welcome_encoded.mp4#t=0.5"
                >
                  <source src="/assets/comment8/welcome_encoded.mp4" type="video/mp4" />
                </video>
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-20 h-20 rounded-full bg-c8-orange/90 flex items-center justify-center shadow-2xl transform transition-transform hover:scale-110">
                    <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop: Autoplay loop */
              <video autoPlay loop muted playsInline className="w-full h-auto">
                <source src="/assets/comment8/welcome_encoded.mp4" type="video/mp4" />
              </video>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ============ CTA ============
const CallToAction = ({ onJoinWaitlist }: { onJoinWaitlist: () => void }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  return (
    <section ref={ref} id="cta" className="py-24 md:py-32 bg-c8-bg overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-c8-orange/5 rounded-full blur-[150px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.8 }} className="text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-c8-text mb-6">
              Start commenting where it <span className="text-c8-orange">counts</span>
            </h2>
            <p className="text-xl text-c8-text-secondary mb-10 max-w-lg mx-auto lg:mx-0">
              Join thousands of deep readers, thinkers, and explainers who refuse to let their best thoughts disappear.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
              <button onClick={onJoinWaitlist} className="btn-primary w-full sm:w-auto">Join Waitlist</button>
              <button onClick={onJoinWaitlist} className="btn-secondary w-full sm:w-auto">iOS App</button>
              <button onClick={onJoinWaitlist} className="btn-secondary w-full sm:w-auto">Android App</button>
            </div>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-6 text-sm text-c8-text-secondary">
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>Free to use
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>Privacy first
              </span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.8, delay: 0.2 }} className="relative flex justify-center">
            <div className="absolute -inset-8 bg-gradient-to-tr from-c8-orange/20 to-orange-200/10 rounded-[3rem] blur-3xl" />
            <img src="/assets/comment8/mobile-view.png" alt="Comment8 mobile" className="relative w-64 md:w-80 drop-shadow-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============ FOOTER ============
const Footer = () => {
  return (
    <footer className="relative bg-c8-bg-alt border-t border-gray-100 py-16">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/3">
          <span className="text-[15vw] font-bold text-gray-100/50 whitespace-nowrap select-none">Comment8</span>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-8">
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 text-center sm:text-left">
            <img src="/assets/comment8/logo.svg" alt="Comment8" className="h-8" />
            <span className="text-c8-text-secondary text-sm sm:text-base">Copyright 2026 Comment8 by Sokhey LLC</span>
          </div>

          {/* Made in California */}
          <div className="flex items-center space-x-2 text-c8-text-secondary text-sm">
            <img
              src="/assets/comment8/calibear.jpg"
              alt="California Bear"
              className="h-5 w-auto"
            />
            <span>Made in California</span>
          </div>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-c8-text-secondary">
            <a href="/privacy-policy" className="hover:text-c8-orange transition-colors py-2">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-c8-orange transition-colors py-2">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// ============ WAITLIST MODAL ============
const WaitlistModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [email, setEmail] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [honeypot, setHoneypot] = useState('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (honeypot) {
      // Bot detected
      return;
    }

    if (email && agreed) {
      // Handle submission here
      try {
        await axios.post(baseURL + '/notify', {
          email,
          source: 'homepage',
          consent: agreed
        });

        setEmail('');
        setAgreed(false)
      } finally {
        onClose()
      }
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="rounded-2xl p-8 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #2A2A2A 0%, #1E1E1E 50%, #252525 100%)',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Content */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-[#D4A574] mb-2">Be first to know</h2>
                  <p className="text-gray-400 text-sm">Request Early Access to Comment8</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email input */}
                  <div
                    className="rounded-lg overflow-hidden"
                    style={{
                      background: 'linear-gradient(145deg, #1A1A1A 0%, #222222 100%)',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    {/* Honeypot field - hidden from users */}
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      style={{ display: 'none' }}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-4 py-4 bg-transparent text-white placeholder-gray-500 outline-none text-base"
                      style={{ fontSize: '16px' }}
                    />
                  </div>

                  {/* Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="relative mt-0.5">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-solid border flex items-center justify-center transition-colors ${
                          agreed ? 'bg-c8-orange border-c8-orange' : 'border-gray-500 bg-transparent'
                        }`}
                      >
                        {agreed && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-400 text-sm leading-tight">
                    I agree to receive a one-time launch email and occasional updates. You can unsubscribe anytime.
                  </span>
                  </label>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={!email || !agreed}
                    className="w-full py-4 rounded-lg font-semibold text-white transition-all duration-300 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: email && agreed
                        ? 'linear-gradient(135deg, #D4A574 0%, #B8956A 50%, #8B6914 100%)'
                        : 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)',
                      boxShadow: email && agreed
                        ? '0 4px 15px rgba(212, 165, 116, 0.3)'
                        : 'none',
                    }}
                  >
                    Notify me at launch
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ============ MAIN APP ============
function App() {
  const { scrollYProgress } = useScroll()
  const [showWaitlist, setShowWaitlist] = useState(false)

  return (
    <div className="relative">
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-c8-orange origin-left z-50"
                  style={{ scaleX: scrollYProgress }} />
      <Navbar onJoinWaitlist={() => setShowWaitlist(true)} />
      <main>
        <HeroQuestions />
        <CinematicTrailer />
        <SolutionReveal onJoinWaitlist={() => setShowWaitlist(true)} />
        <HowItWorks />
        <FeatureBreakdown />
        <WhyComment8 />
        <CallToAction onJoinWaitlist={() => setShowWaitlist(true)} />
        <Footer />
      </main>
      <WaitlistModal isOpen={showWaitlist} onClose={() => setShowWaitlist(false)} />
    </div>
  )
}

export default App
